import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  HomeIcon, 
  ShoppingCartIcon, 
  ComputerDesktopIcon,
  ChartBarIcon,
  CogIcon,
  XMarkIcon,
  CubeIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ClipboardDocumentListIcon,
  FolderIcon,
  UserIcon,
  ClipboardDocumentCheckIcon,
  CalculatorIcon,
  BriefcaseIcon,
  CalendarIcon,
  // BuildingOfficeIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '../../contexts/AuthContext'

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
  currentPath: string
  isCollapsed?: boolean
}

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon, permission: 'reports.sales.read' },
  { name: 'POS', href: '/pos', icon: ShoppingCartIcon, permission: 'pos.orders.create' },
  { name: 'Orders', href: '/orders', icon: ClipboardDocumentCheckIcon, permission: 'pos.orders.read' },
  { name: 'Table Management', href: '/tables', icon: TableCellsIcon, permission: 'tables.read' },
  { name: 'Reservations', href: '/reservations', icon: CalendarIcon, permission: 'tables.read' },
  { name: 'Waiter Module', href: '/waiter', icon: UserIcon, permission: 'waiter.orders.create' },
  { name: 'Kitchen Display', href: '/kds', icon: ComputerDesktopIcon, permission: 'kds.view' },
  { name: 'Menu Management', href: '/menu', icon: ClipboardDocumentListIcon, permission: 'menu.items.read' },
  { name: 'Categories', href: '/categories', icon: FolderIcon, permission: 'menu.categories.read' },
  { name: 'Inventory', href: '/inventory', icon: CubeIcon, permission: 'inventory.read' },
  { name: 'Reports', href: '/reports', icon: ChartBarIcon, permission: 'reports.sales.read' },
  { name: 'Customers', href: '/customers', icon: UserGroupIcon, permission: 'customers.read' },
  { name: 'Accounting', href: '/accounting', icon: CalculatorIcon, permission: 'accounting.accounts.read' },
  { name: 'HR Management', href: '/hr', icon: BriefcaseIcon, permission: 'hr.employees.read' },
  { name: 'Payments', href: '/payments', icon: CurrencyDollarIcon, permission: 'pos.orders.read' },
  { name: 'Invoices', href: '/invoices', icon: DocumentTextIcon, permission: 'accounting.invoices.manage' },
  { name: 'Settings', href: '/settings', icon: CogIcon, permission: 'settings.restaurant.read' },
]

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, currentPath, isCollapsed: externalCollapsed }) => {
  const { hasPermission } = useAuth()
  const [isHovered, setIsHovered] = useState(false)
  
  // Sidebar expands on hover, collapses when not hovered
  const baseCollapsed = externalCollapsed !== undefined ? externalCollapsed : true
  const isCollapsed = baseCollapsed && !isHovered

  // Filter navigation items based on permissions
  const filteredNavigation = navigation.filter(item => {
    if (!item.permission) return true // Show items without permission requirement
    return hasPermission(item.permission)
  })

  return (
    <>
      {/* Mobile sidebar overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
        </div>
      )}

      {/* Sidebar */}
      <div 
        className={`
          fixed inset-y-0 left-0 z-50 bg-white dark:bg-gray-800 shadow-lg transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col h-screen
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-20' : 'w-64'}
        `}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className={`flex items-center justify-between h-16 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 ${isCollapsed ? 'px-3' : 'px-6'}`}>
          {!isCollapsed && (
            <div className="flex items-center flex-1">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">M</span>
                </div>
              </div>
              <div className="ml-3">
                <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                  POS by Mufaza
                </h1>
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className="flex items-center justify-center w-full">
              <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">M</span>
              </div>
            </div>
          )}
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <nav className={`flex-1 overflow-y-auto overflow-x-hidden mt-6 scrollbar-hide ${isCollapsed ? 'px-2' : 'px-3'}`}>
          <div className="space-y-1 pb-4">
            {filteredNavigation.map((item) => {
              const isActive = currentPath === item.href
              return (
                <div key={item.name} className="relative group">
                  <Link
                    to={item.href}
                    className={`
                      group flex items-center text-sm font-medium rounded-md transition-colors duration-200
                      ${isCollapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'}
                      ${isActive 
                        ? 'bg-primary-100 text-primary-900 dark:bg-primary-900 dark:text-primary-100' 
                        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white'
                      }
                    `}
                    onClick={() => {
                      // Close mobile sidebar when navigating
                      if (window.innerWidth < 1024) {
                        onClose()
                      }
                    }}
                    title={isCollapsed ? item.name : undefined}
                  >
                    <item.icon 
                      className={`
                        ${isCollapsed ? 'h-6 w-6' : 'mr-3 h-5 w-5'} flex-shrink-0
                        ${isActive 
                          ? 'text-primary-500' 
                          : 'text-gray-400 group-hover:text-gray-500 dark:text-gray-500 dark:group-hover:text-gray-400'
                        }
                      `}
                    />
                    {!isCollapsed && (
                      <span className="truncate">{item.name}</span>
                    )}
                  </Link>
                  {/* Tooltip for collapsed state */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200 whitespace-nowrap z-50">
                      {item.name}
                      <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900 dark:border-r-gray-700"></div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </nav>

        {/* Footer with Version */}
        <div className="flex-shrink-0 border-t border-gray-200 dark:border-gray-700">
          {!isCollapsed && (
            <div className="p-4">
              <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Version 13.0.2
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className="p-2">
              <div className="text-[10px] text-gray-500 dark:text-gray-400 text-center">
                v13.0.2
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default Sidebar
